<template>
  <header class="app-header">

    <!-- ── Row 1: always visible ─────────────────────────────────────────────── -->
    <div class="header-row">

      <!-- Logo -->
      <button @click="$emit('go-cliente')" class="header-logo-btn">
        <Logo :companyName="companyName" />
      </button>

      <!-- Search (only outside cliente screen) -->
      <div v-if="screen !== 'cliente'" class="header-search">
        <Icon name="search" :size="15" color="var(--text-2)" />
        <input
          :value="search"
          @input="$emit('search', $event.target.value)"
          placeholder="Buscar productos…"
          class="header-search__input"
        />
        <button v-if="search" @click="$emit('search', '')" class="header-search__clear">
          <Icon name="close" :size="13" />
        </button>
      </div>

      <!-- Spacer: pushes right items to the edge -->
      <div class="header-spacer" />

      <!-- Active comedores badge -->
      <div v-if="screen !== 'cliente' && activeCount > 0" class="header-active-badge">
        <span class="header-active-badge__dot" />
        <span class="header-active-badge__text">
          {{ activeCount }} comedor{{ activeCount !== 1 ? 'es' : '' }} activo{{ activeCount !== 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Mis órdenes -->
      <button @click="$emit('go-orders')" class="header-orders-btn">
        <Icon name="receipt" :size="14" />
        <span class="header-orders-btn__label">Mis órdenes</span>
      </button>

      <!-- User badge + logout — avatar is ALWAYS visible -->
      <div class="header-user">
        <span class="header-user__name">{{ displayName }}</span>
        <button
          @click="$emit('logout')"
          :title="`Cerrar sesión (${displayName})`"
          class="header-user__avatar"
        >{{ initials }}</button>
      </div>

    </div>

    <!-- ── Row 2: Steps + ModeSwitch (only on pedido / checkout / confirmacion) -->
    <div v-if="showSteps" class="header-subrow">
      <Steps :step="screen" />
      <template v-if="screen === 'pedido'">
        <div class="header-divider" />
        <ModeSwitch :mode="mode" @update:mode="$emit('update:mode', $event)" />
      </template>
    </div>

  </header>
</template>

<script setup>
import { computed } from 'vue'
import Logo       from './ui/Logo.vue'
import Icon       from './ui/Icon.vue'
import Steps      from './ui/Steps.vue'
import ModeSwitch from './ModeSwitch.vue'

const props = defineProps({
  search:      { type: String, default: '' },
  activeCount: { type: Number, default: 0 },
  screen:      { type: String, default: 'cliente' },
  mode:        { type: String, default: 'tarjetas' },
  userName:    { type: String, default: '' },
  companyName: { type: String, default: '' },
})

defineEmits(['search', 'go-orders', 'go-cliente', 'logout', 'update:mode'])

const showSteps = computed(() => ['pedido', 'checkout', 'confirmacion'].includes(props.screen))

const displayName = computed(() => props.userName || 'Usuario')
const initials    = computed(() => {
  const parts = displayName.value.trim().split(' ').filter(Boolean)
  if (!parts.length) return 'U'
  return parts.map(p => p[0].toUpperCase()).slice(0, 2).join('')
})
</script>

<style scoped>
/* ─── Header shell (column) ──────────────────────────────────────────────────── */
.app-header {
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

/* ─── Row 1 ──────────────────────────────────────────────────────────────────── */
.header-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 24px;
  min-width: 0;
}

.header-spacer {
  flex: 1;
}

/* ─── Row 2 — Steps + ModeSwitch ────────────────────────────────────────────── */
.header-subrow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 8px 24px 10px;
  border-top: 1px solid var(--border);
}

.header-divider {
  width: 1px;
  height: 20px;
  background: var(--border-strong);
  flex-shrink: 0;
}

/* ─── Logo button ────────────────────────────────────────────────────────────── */
.header-logo-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
}

/* ─── Search box ─────────────────────────────────────────────────────────────── */
.header-search {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  background: var(--bg);
  border-radius: 8px;
  width: 220px;
  flex-shrink: 1;
  min-width: 100px;
  transition: width 0.2s;
}

.header-search__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 0 8px;
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text);
  min-width: 0;
}

.header-search__clear {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-3);
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* ─── Active comedores badge ─────────────────────────────────────────────────── */
.header-active-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--primary-soft);
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--primary);
  font-weight: 700;
  flex-shrink: 0;
  white-space: nowrap;
}

.header-active-badge__dot {
  width: 6px;
  height: 6px;
  background: var(--primary);
  border-radius: 999px;
  display: inline-block;
  flex-shrink: 0;
}

/* ─── Orders button ──────────────────────────────────────────────────────────── */
.header-orders-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 13px;
  color: var(--text-2);
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ─── User badge + logout ────────────────────────────────────────────────────── */
.header-user {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 4px 12px;
  border-radius: 999px;
  background: var(--bg);
  font-family: var(--ff-body);
  font-size: 13px;
  color: var(--text-2);
  flex-shrink: 0;
  white-space: nowrap;
}

/* Avatar is the logout trigger — must always be visible */
.header-user__avatar {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: var(--primary);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-family: var(--ff-head);
  font-size: 11px;
  flex-shrink: 0;
  transition: background 0.15s;
}

.header-user__avatar:hover {
  background: var(--primary-hover);
}

/* ─── Responsive breakpoints (row 1 only — row 2 has ample room at any width) ─ */

/* 980px: shrink search, drop active badge text, orders icon-only */
@media (max-width: 980px) {
  .header-row                { gap: 8px; padding: 10px 16px; }
  .header-search             { width: 160px; }
  .header-active-badge__text { display: none; }
  .header-active-badge       { padding: 5px 8px; }
  .header-orders-btn__label  { display: none; }
  .header-orders-btn         { padding: 0 10px; }
}

/* 820px: hide search, strip username, simplify user badge */
@media (max-width: 820px) {
  .header-search     { display: none; }
  .header-active-badge { display: none; }
  .header-user__name { display: none; }
  .header-user       { padding: 0; background: transparent; gap: 0; }
}

/* 640px: collapse subrow to stack when even that's tight */
@media (max-width: 640px) {
  .header-subrow     { flex-wrap: wrap; gap: 12px; }
  .header-divider    { display: none; }
}
</style>
